package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText

import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/single_questions")
@Composable
fun SingleQuestionsPage() {
    BlogLayout(
        PageProperties(
            "Single Interview Question",
            "Top Kotlin Coroutines interview questions asked to experienced/fresher candidates during interviews at various organizations."
        ),
        BlogProperties("Coroutines", "Single Interview Question")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ol {
                //Flow
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is a common pitfall of using flowOn incorrectly?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It only changes the context of upstream operators, not downstream (collector) work.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //shareIn
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does shareIn(scope, started, replay) do to a cold Flow?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Converts it to a hot SharedFlow shared within the given scope.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does stateIn(scope, started, initialValue) guarantee?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It turns a Flow into a StateFlow with an initial value and shared upstream collection in the scope.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //suspend
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does suspend mean on a function?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It can suspend without blocking the thread and must be called from a coroutine or another suspend function. suspend functions can suspend the coroutine without blocking the underlying thread and require a coroutine context to call.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "In a suspendCancellableCoroutine block, what must you ensure when integrating a callback API?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Unregister/cancel the callback on coroutine cancellation to avoid leaks and double-resume. You must handle cancellation by disposing callbacks and ensure you resume exactly once.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //Combine & Zip
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does zip(A,B) do compared to combine(A,B)?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Pairs emissions one-to-one by index; waits for both to emit the next element",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "You have two flows: A emits quickly, B emits slowly. You use combine(A,B). When does combine emit?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "After each emission from either flow, once both have emitted at least one value combine emits on any upstream emission after both have initial values.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //launch, async, await
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the main difference between launch and async?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "launch returns a Job; async returns a Deferred<T>",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "A coroutine started with async fails with an exception and you never await it. What is the risk?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The exception can be lost or reported to the CoroutinExceptionHandler depending on context; always await or use structured scope. Deferred exceptions are observed on await; unobserved failures can be tricky and lead to silent crashes/logging depending on scope.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //Flow Operator
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the common mistake when using onEach { } without a terminal operator?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Nothing runs because Flow is cold; you must collect or use launchIn. Intermediate operators do nothing until a terminal operator starts collection.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "If you collect a Flow twice, what happens by default (cold flow)?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The upstream is executed twice, once per collector. Cold flows run per-collector; use shareIn/stateIn to share work.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which operator is terminal for Flow?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "collect",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does debounce(300) do on a Flow of text changes?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Emits only after 300ms of silence since the latest value (useful for search)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does sample(300) do compared to debounce(300)?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Emits the latest value periodically every 300ms while values keep coming; debounce waits for silence",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does flatMapConcat guarantee?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Processes inner flows sequentially, preserving order (next starts after previous completes).",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "In a Flow chain, where is map{} executed relative to flowOn?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "If map is upstream of flowOn, it runs in the dispatcher specified by flowOn",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does onEach { } do in Flow?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Performs a side-effect for each emission and returns a new Flow (still needs collection).",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "You have flow { emit(1); emit(2) } and you call .map { throw RuntimeException() }.catch { emit(-1) }. What is emitted?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "-1",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does catch{} in Flow catch?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Exceptions from upstream flow operators, but not exceptions thrown in the collector. catch handles upstream exceptions; exceptions in collect/collector should be handled with try/catch around collect.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "In a Flow, what does onCompletion {} receive when the flow is cancelled?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A CancellationException as the cause (or a subclass), typically non-null. onCompletion is invoked on normal completion and cancellation; cancellation passes a CancellationException cause.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "In Flow, what is the key difference between buffer() and conflate()?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "buffer queues all values up to capacity; conflate drops intermediate values keeping only the latest. buffer introduces a channel buffer; conflate skips intermediate emissions when collector is slow.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "In Kotlin Flow, the code inside a flow {} builder runs when:",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A terminal operator collects the flow. Flows are cold by default; the block runs only on collection by a terminal operator like collect.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the most correct way to handle exceptions thrown inside collect{}?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Wrap the collect call in try/catch (catch operator won't catch collector exceptions)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "runCatching",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why is it a bug to call emit from a different coroutine than the one running inside flow {}?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Flow builder is not thread-safe; use channelFlow for concurrent emissions. flow { } is designed for sequential emissions from a single coroutine; concurrent emissions require channelFlow.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "runCatching",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //mapLatest
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the subtle difference between mapLatest and flatMapLatest when transforming a Flow?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "mapLatest cancels the previous transform block but keeps a single output value; flatMapLatest cancels previous inner flow collections and switches to the latest inner flow.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why can using flatMapMerge lead to out-of-order results?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Inner flows run concurrently; emissions interleave based on completion timing. flatMapMerge merges concurrently; order isn't guaranteed.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the difference between collectLatest and mapLatest + collect?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "collectLatest cancels the body of the collector for previous emissions; mapLatest cancels mapping work but not necessarily collector work after mapping. collectLatest cancels previous collector block; mapLatest cancels transform block upstream.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //supervisorScope & coroutineScope
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why might you prefer supervisorScope in a concurrent fetch of multiple endpoints?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "One failing child won't cancel siblings; you can collect partial results",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does coroutineScope { ... } do that supervisorScope { ... } does not?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It cancels all children if one child fails. In coroutine Scope, a child failure cancels the whole scope (structured concurrency). In supervisorScope, siblings can continue.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //callbackFlow
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "When using callbackFlow, what is required to avoid resource leaks?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Call awaitClose {...} to unregister callbacks/close resources",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does channelFlow provide that flow { } does not?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Safe concurrent emissions from multiple coroutines via a channel-backed producer. channelFlow allows sending from multiple coroutines and bridges callback-style APIs safely.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //Job
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "If you call cancel() on a parent Job, what happens to its children by default?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "All children are cancelled (unless using SupervisorJob/supervisorScope semantics). Structured concurrency cancels child jobs when the parent is cancelled, unless supervision is used",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //Exception
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which is true about cancellation in coroutines?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Cancellation is cooperative and checked at suspension points (or via ensureActive/isActive)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why is using CoroutineExceptionHandler on a child coroutine often ineffective?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It only handles uncaught exceptions in root coroutines; children propagate to parent unless supervised.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why can using runBlocking on Android's main thread be catastrophic?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It blocks the main thread, potentially freezing the Ul and causing ANRs",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does ensureActive() do inside a CPU loop?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Throws CancellationException if the coroutine is cancelled, making CPU loops responsive to cancellation",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why can you still get leaks if you collect a Flow in a Fragment using lifecycleScope.launch { flow.collect { ... } } without repeatOnLifecycle?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Collection can continue across STOPPED state; repeatOnLifecycle cancels/restarts collection with lifecycle.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What happens if an exception is thrown inside a flow builder and not caught?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It cancels collection and is propagated to the collector.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //Dispatchers
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What does withContext(Dispatchers.IO) guarantee?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Switches coroutine context for the block; it may resume on the original context after completion",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //StateFlow
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why is StateFlow a bad fit for one-off events like navigation or toasts (without extra handling)?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It replays the latest value to new collectors, causing events to re-trigger after configuration changes.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What can happen if you use mutableStateFlow.value++ from multiple threads without protection?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Lost updates due to non-atomic read-modify-write; use update{} for atomic update.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is a subtle pitfall of using SharedFlow with replay > 0 for events?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "New collectors may receive old events again (replayed), causing duplicate handling",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which statement about SharedFlow is correct?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "SharedFlow is hot and can replay a configurable number of past values to new collectors",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is a key property of StateFlow compared to SharedFlow?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "StateFlow always has a current value and replays exactly one latest value to new collectors.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which statement about StateFlow emissions is correct?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It is conflated: slow collectors skip intermediate values and always get the latest state. StateFlow is conflated by design; it's for state, not events.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //ROOM
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "When using Flow with Room, why might you add distinctUntilChanged()?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Room may emit identical values for the same query result; this avoids redundant Ul updates.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why should you use an AutoMigration (Room 2.4+)?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It generates simple migration code for you if schema changes are simple.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which method is used to add a new column in a migration SQL?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "ALTER TABLE X ADD COLUMN",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is fallbackToDestructiveMigration()?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Deletes and recreates the database if no migration is found",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How do you define a Migration in Room?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A Migration object that defines start and end versions and SQL statements. A migration in Room is the process of updating your app's SQLite database schema when entity classes are modified, without losing existing user data.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What happens if you change your Room entity and increment the version without providing a migration?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Database is wiped",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What happens when a coroutine is cancelled?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // Cancellation is COOPERATIVE — code must check for it
                        suspend fun processItems(items: List<Item>) {
                            for (item in items) {
                                ensureActive()  // Throws CancellationException if cancelled
                                heavyComputation(item)
                            }
                        }

                        // NEVER swallow CancellationException
                        try {
                            doWork()
                        } catch (e: CancellationException) {
                            throw e  // ALWAYS re-throw!
                        } catch (e: Exception) {
                            handleError(e)
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Flow exception handling, testing coroutines with runTest + Turbine, SupervisorJob patterns.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // Q18: Flow error handling
                        repository.getProducts()
                            .retryWhen { cause, attempt ->
                                cause is IOException && attempt < 3  // Retry up to 3 times for network errors
                            }
                            .catch { emit(emptyList()) }  // Fallback on final failure
                            .collect { /* ... */ }

                        // Q19: Testing with runTest + Turbine
                        @Test
                        fun `products emit success`() = runTest {
                            val viewModel = ProductViewModel(FakeRepository(testProducts))
                            viewModel.state.test {
                                assertEquals(UiState.Loading, awaitItem())
                                assertEquals(UiState.Success(testProducts), awaitItem())
                            }
                        }
                        // Q20: MainDispatcherRule for ViewModel tests
                        class MainDispatcherRule(
                            private val dispatcher: TestDispatcher = UnconfinedTestDispatcher()
                        ) : TestWatcher() {
                            override fun starting(description: Description) { Dispatchers.setMain(dispatcher) }
                            override fun finished(description: Description) { Dispatchers.resetMain() }
                        }
                    """.trimIndent()
                )
            }
        }
    }
}